import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

// Pages
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ChangePasswordPage from "@/pages/ChangePasswordPage";
import RoleSelectionPage from "@/pages/RoleSelectionPage";
import Dashboard from "@/pages/Dashboard";
import NewApplication from "@/pages/NewApplication";
import ApplicationSubmittedPage from "@/pages/ApplicationSubmittedPage";
import TenantInfoUpdatePage from "@/pages/TenantInfoUpdatePage";
import TenantPaymentPortal from "@/pages/TenantPaymentPortal";
import UserProfilePage from "@/pages/UserProfilePage";
import AdminRegistrants from "@/pages/AdminRegistrants";
import AdminTenants from "@/pages/AdminTenants";
import AdminPendingApproval from "@/pages/AdminPendingApproval";
import AdminTenantInfoPage from "@/pages/AdminTenantInfoPage";
import AdminPaymentReminders from "@/pages/AdminPaymentReminders";
import AdminPaymentConfirmations from "@/pages/AdminPaymentConfirmations";
import AdminPaymentReports from "@/pages/AdminPaymentReports";
import AdminApplicationLinks from "@/pages/AdminApplicationLinks";
import AdminMessagesPage from "@/pages/AdminMessagesPage";
import AdminSecurityCameras from "@/pages/AdminSecurityCameras";
import AdminFormView from "@/pages/AdminFormView";
import AdminApplicationFormView from "@/pages/AdminApplicationFormView";
import AdminPropertyList from "@/pages/AdminPropertyList";
import AdminTenantDatabase from "@/pages/AdminTenantDatabase";
import TenantMessagesPage from "@/pages/TenantMessagesPage";
import PublicApplicationForm from "@/pages/PublicApplicationForm";
import PublicRegisterInvite from "@/pages/PublicRegisterInvite";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem("token");
      if (storedToken) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${storedToken}` }
          });
          setUser(response.data);
          setToken(storedToken);
        } catch (error) {
          localStorage.removeItem("token");
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    initAuth();
  }, []);

  const login = async (username, password) => {
    const response = await axios.post(`${API}/auth/login`, { username, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem("token", newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const register = async (data) => {
    const response = await axios.post(`${API}/auth/register`, data);
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem("token", newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const logout = async () => {
    // Mark user as offline before logging out
    if (token) {
      try {
        await axios.post(`${API}/auth/logout`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (error) {
        // Ignore errors - we're logging out anyway
      }
    }
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
  };

  const updateUser = (userData) => {
    setUser(userData);
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false, allowManager = false }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-primary">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check if user needs to change password
  if (user.must_change_password && location.pathname !== "/change-password") {
    return <Navigate to="/change-password" replace />;
  }

  // Check if user needs to select role (first time login after registration approval)
  if (user.role !== "admin" && user.registration_approved && !user.has_selected_role && 
      location.pathname !== "/select-role" && location.pathname !== "/change-password") {
    return <Navigate to="/select-role" replace />;
  }

  // Admin only routes
  if (adminOnly) {
    if (allowManager) {
      if (user.role !== "admin" && user.role !== "manager") {
        return <Navigate to="/dashboard" replace />;
      }
    } else {
      if (user.role !== "admin") {
        return <Navigate to="/dashboard" replace />;
      }
    }
  }

  return children;
};

// Public Route (redirect if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-primary">Loading...</div>
      </div>
    );
  }

  if (user) {
    if (user.must_change_password) {
      return <Navigate to="/change-password" replace />;
    }
    // Check if user needs to select role
    if (user.role !== "admin" && user.registration_approved && !user.has_selected_role) {
      return <Navigate to="/select-role" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
      
      {/* Protected Routes */}
      <Route path="/change-password" element={<ProtectedRoute><ChangePasswordPage /></ProtectedRoute>} />
      <Route path="/select-role" element={<ProtectedRoute><RoleSelectionPage /></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><UserProfilePage /></ProtectedRoute>} />
      <Route path="/new-application" element={<ProtectedRoute><NewApplication /></ProtectedRoute>} />
      <Route path="/application-submitted" element={<ProtectedRoute><ApplicationSubmittedPage /></ProtectedRoute>} />
      <Route path="/tenant-info-update" element={<ProtectedRoute><TenantInfoUpdatePage /></ProtectedRoute>} />
      <Route path="/payment-portal" element={<ProtectedRoute><TenantPaymentPortal /></ProtectedRoute>} />
      <Route path="/messages" element={<ProtectedRoute><TenantMessagesPage /></ProtectedRoute>} />
      
      {/* Admin/Manager Routes */}
      <Route path="/admin/registrants" element={<ProtectedRoute adminOnly allowManager><AdminRegistrants /></ProtectedRoute>} />
      <Route path="/admin/tenants" element={<ProtectedRoute adminOnly allowManager><AdminTenants /></ProtectedRoute>} />
      <Route path="/admin/pending-approval" element={<ProtectedRoute adminOnly allowManager><AdminPendingApproval /></ProtectedRoute>} />
      <Route path="/admin/tenant-info" element={<ProtectedRoute adminOnly allowManager><AdminTenantInfoPage /></ProtectedRoute>} />
      <Route path="/admin/payment-reminders" element={<ProtectedRoute adminOnly allowManager><AdminPaymentReminders /></ProtectedRoute>} />
      <Route path="/admin/payment-confirmations" element={<ProtectedRoute adminOnly allowManager><AdminPaymentConfirmations /></ProtectedRoute>} />
      <Route path="/admin/reports" element={<ProtectedRoute adminOnly allowManager><AdminPaymentReports /></ProtectedRoute>} />
      <Route path="/admin/application-links" element={<ProtectedRoute adminOnly allowManager><AdminApplicationLinks /></ProtectedRoute>} />
      <Route path="/admin/messages" element={<ProtectedRoute adminOnly allowManager><AdminMessagesPage /></ProtectedRoute>} />
      <Route path="/admin/security-cameras" element={<ProtectedRoute adminOnly><AdminSecurityCameras /></ProtectedRoute>} />
      <Route path="/admin/form-view" element={<ProtectedRoute adminOnly><AdminFormView /></ProtectedRoute>} />
      <Route path="/admin/form-view/application" element={<ProtectedRoute adminOnly><AdminApplicationFormView /></ProtectedRoute>} />
      <Route path="/admin/properties" element={<ProtectedRoute adminOnly><AdminPropertyList /></ProtectedRoute>} />
      <Route path="/admin/tenant-database" element={<ProtectedRoute adminOnly><AdminTenantDatabase /></ProtectedRoute>} />
      
      {/* Public Application Routes (no auth required) */}
      <Route path="/apply/:token" element={<PublicApplicationForm />} />
      <Route path="/register-invite/:token" element={<PublicRegisterInvite />} />
      
      {/* Default Route */}
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
